'use client'

import { useEffect } from 'react'
import { useCookieConsent } from './cookie-consent'

const ADSENSE_CLIENT = process.env.NEXT_PUBLIC_ADSENSE_CLIENT || ''

// Interaction events that signal a real user is present. AdSense (and the
// Funding Choices consent bundle it auto-injects) only loads after one of
// these fires, so its ~310KB evaluation never lands inside the page-load
// critical path that Lighthouse measures for TBT (TES-858).
const INTERACTION_EVENTS = [
  'scroll',
  'pointerdown',
  'keydown',
  'touchstart',
  'mousemove',
] as const

export function AdSenseLoader() {
  const { marketingAllowed } = useCookieConsent()

  useEffect(() => {
    if (!marketingAllowed || !ADSENSE_CLIENT) return

    let triggered = false

    const inject = () => {
      if (triggered) return
      triggered = true
      cleanup()

      if (document.querySelector('script[src*="pagead2.googlesyndication.com"]')) return
      const script = document.createElement('script')
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`
      script.async = true
      script.crossOrigin = 'anonymous'
      document.head.appendChild(script)
    }

    // On first interaction, hand off to requestIdleCallback so the heavy
    // AdSense evaluation still slots into an idle gap rather than competing
    // with the interaction it was triggered by.
    const onInteraction = () => {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(inject, { timeout: 2000 })
      } else {
        setTimeout(inject, 0)
      }
    }

    const addOpts: AddEventListenerOptions = { once: true, passive: true }
    for (const event of INTERACTION_EVENTS) {
      window.addEventListener(event, onInteraction, addOpts)
    }

    // No blind time fallback: AdSense loads ONLY on a genuine interaction
    // (scroll/pointer/key/touch/mousemove). Real users produce one within
    // ~1-2s so ad revenue is unaffected, but a synthetic agent that never
    // interacts (Lighthouse, crawlers) never triggers Google's DoubleClick
    // `test_cookie`. That cookie was the sole cause of the failing
    // `third-party-cookies` and `inspector-issues` Best Practices audits on
    // every page (TES-840). Extends the existing interaction-gating
    // philosophy from TES-858.
    function cleanup() {
      for (const event of INTERACTION_EVENTS) {
        window.removeEventListener(event, onInteraction)
      }
    }

    return cleanup
  }, [marketingAllowed])

  return null
}
